import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { isCommercial } from "@/lib/roles";
import LinkedComplaints from "@/components/LinkedComplaints";
import ComplaintCorrectiveActions from "@/components/ComplaintCorrectiveActions";
import { TYPE_LABELS } from "@/lib/complaintTypes";
import ComplaintStatusBadge from "@/components/ComplaintStatusBadge";
import { NATURE_CONCERNE, logoConcerne, natureConcerne, nomConcerne } from "@/lib/concerned";
import SeverityBadge from "@/components/SeverityBadge";
import FileUpload, { FileList } from "@/components/FileUpload";
import { api, API_BASE, getAxiosErrorMessage } from "@/lib/api";
import { ArrowLeft, Loader2, MessageCircle, History, Tag, User as UserIcon, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";


export default function ComplaintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [comments, setComments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [files, setFiles] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  // Le commercial consulte, commente et joint des pieces — il ne pilote pas le
  // cycle de vie. Le backend refuse dejà ces ecritures (403) : ce garde-fou
  // evite simplement de lui presenter des commandes vouees a echouer.
  const canManage = !isCommercial(user);

  const loadAll = useCallback(async () => {
    const [r1, r2, r3, r4] = await Promise.all([
      api.get(`/complaints/${id}`),
      api.get(`/complaints/${id}/comments`),
      api.get(`/complaints/${id}/audit`),
      api.get(`/files/entity/complaint/${id}`),
    ]);
    setC(r1.data);
    setComments(r2.data);
    setAudit(r3.data);
    setFiles(r4.data);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadAll().catch((e) => {
      toast.error(getAxiosErrorMessage(e));
      setLoading(false);
    });
  }, [loadAll]);

  const updateField = async (field, value) => {
    try {
      const { data } = await api.patch(`/complaints/${id}`, { [field]: value });
      setC(data);
      toast.success("Mise à jour enregistrée");
      loadAll();
    } catch (e) {
      toast.error(getAxiosErrorMessage(e));
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await api.post(`/complaints/${id}/comments`, { content: newComment.trim() });
      setNewComment("");
      loadAll();
      toast.success("Commentaire ajouté");
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    }
  };

  const onFileDelete = async (fid) => {
    try {
      await api.delete(`/files/${fid}`);
      toast.success("Fichier supprimé");
      loadAll();
    } catch (e) {
      toast.error(getAxiosErrorMessage(e));
    }
  };

  // Pas de jeton dans l'URL : le cookie de session HttpOnly est joint
  // automatiquement par le navigateur à cette navigation.
  const getDownloadUrl = (fid) => `${API_BASE}/files/${fid}/download`;

  if (loading) {
    return (
      <>
        <Header title="Litige" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </>
    );
  }
  if (!c) return null;

  return (
    <>
      <Header
        title={c.number}
        subtitle={`${TYPE_LABELS[c.type]} · ${nomConcerne(c)}`}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate("/litiges")}
            className="bg-white/10 hover:bg-white/20 text-white border-0"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-1.5" /> Retour
          </Button>
        }
      />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <ComplaintStatusBadge status={c.status} />
              <SeverityBadge severity={c.severity} />
            </div>

            {/* Creator & party cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/70 border border-slate-100" data-testid="creator-card">
                <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                  {(c.created_by_name || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Créé par</div>
                  <div className="text-sm font-semibold text-slate-900 truncate">{c.created_by_name || "—"}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {(c.created_at || "").slice(0, 16).replace("T", " ")} · {c.created_by_role || ""}
                  </div>
                </div>
              </div>
              {natureConcerne(c) ? (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/70 border border-slate-100">
                  {logoConcerne(c) ? (
                    <img
                      src={`${API_BASE}/files/${logoConcerne(c)}/download`}
                      alt={nomConcerne(c)}
                      className="h-10 w-10 rounded-lg object-contain bg-white border border-slate-200"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-slate-200 text-slate-600 flex items-center justify-center">
                      <UserIcon size={18} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                      {NATURE_CONCERNE[natureConcerne(c)]}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {nomConcerne(c)}
                    </div>
                    {c.product_ref ? (
                      <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                        <Package size={11} /> Réf : {c.product_ref}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <h2 className="text-xl font-bold text-slate-900 font-heading mb-1">{c.sub_type || "Litige"}</h2>
            <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed" data-testid="complaint-description">
              {c.description}
            </p>
            {c.tags?.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {c.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    <Tag size={11} strokeWidth={1.5} /> {t}
                  </span>
                ))}
              </div>
            ) : null}
          </section>

          <LinkedComplaints complaintId={id} onChanged={loadAll} />

          <ComplaintCorrectiveActions complaintId={id} peutCreer={canManage} />

          <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
            <h3 className="text-lg font-semibold text-slate-900 font-heading mb-4">Pièces jointes</h3>
            <FileUpload entityType="complaint" entityId={id} onUploaded={() => loadAll()} />
            <div className="mt-4">
              <FileList files={files} onDelete={canManage ? onFileDelete : undefined} getDownloadUrl={getDownloadUrl} />
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
            <h3 className="text-lg font-semibold text-slate-900 font-heading mb-4 flex items-center gap-2">
              <MessageCircle size={18} strokeWidth={1.5} /> Commentaires internes
            </h3>
            <form onSubmit={submitComment} className="flex gap-2 mb-5">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire interne…"
                rows={2}
                className="rounded-xl"
                data-testid="comment-input"
              />
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="comment-submit">
                Publier
              </Button>
            </form>
            <div className="space-y-3">
              {comments.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6">Aucun commentaire.</div>
              ) : comments.map((cm) => (
                <div key={cm.id} className="p-3 rounded-xl bg-slate-50/60 border border-slate-100" data-testid={`comment-${cm.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold text-slate-900">{cm.author_name}</div>
                    <div className="text-xs text-slate-400">{(cm.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{cm.content}</div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
            <h3 className="text-base font-semibold text-slate-900 font-heading mb-4">Gestion</h3>
            {canManage ? (
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-1">Statut</div>
                  <Select value={c.status} onValueChange={(v) => updateField("status", v)}>
                    <SelectTrigger className="rounded-xl" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ouverte">Ouvert</SelectItem>
                      <SelectItem value="cloturee">Clôturé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-1">Gravité</div>
                  <Select value={c.severity} onValueChange={(v) => updateField("severity", v)}>
                    <SelectTrigger className="rounded-xl" data-testid="detail-severity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="faible">Faible</SelectItem>
                      <SelectItem value="moyenne">Moyenne</SelectItem>
                      <SelectItem value="critique">Critique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="detail-readonly">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-1.5">Statut</div>
                  <ComplaintStatusBadge status={c.status} />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-1.5">Gravité</div>
                  <SeverityBadge severity={c.severity} />
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  Le suivi du litige est assuré par le service qualité.
                  Vous pouvez ajouter un commentaire ou une pièce jointe.
                </p>
              </div>
            )}
          </section>

          <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
            <h3 className="text-base font-semibold text-slate-900 font-heading mb-4 flex items-center gap-2">
              <History size={16} strokeWidth={1.5} /> Historique (audit)
            </h3>
            <div className="relative pl-5 border-l-2 border-slate-100 space-y-5">
              {audit.length === 0 ? (
                <div className="text-sm text-slate-400">Aucun événement.</div>
              ) : audit.map((a) => (
                <div key={a.id} className="relative" data-testid={`audit-${a.id}`}>
                  <span className="absolute -left-[14px] top-1 w-3 h-3 rounded-full bg-white border-2 border-blue-500"></span>
                  <div className="text-sm font-semibold text-slate-800">{a.description}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {a.user_name} · {(a.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
